// Web avatar picker: opens the native file dialog, reads the chosen image, and
// downscales it to a square ~256px JPEG data URL so it's small enough to store
// on the conversation row (and broadcast later) without a media-upload round
// trip. Returns `null` if the user cancels or the file isn't a usable image.

const MAX_DIM = 256;
const JPEG_QUALITY = 0.82;

export async function pickAvatarDataUrl(): Promise<string | null> {
  if (typeof document === 'undefined') return null;

  const file = await new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    // Safari/Firefox fire no event on cancel; resolve(null) on focus return.
    input.click();
  });
  if (file === null) return null;

  const dataUrl = await readAsDataUrl(file);
  if (dataUrl === null) return null;

  try {
    return await downscaleSquare(dataUrl);
  } catch {
    // If canvas processing fails (e.g. tainted/unsupported), fall back to the
    // raw data URL — better a large avatar than none.
    return dataUrl;
  }
}

function readAsDataUrl(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

function downscaleSquare(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new globalThis.Image();
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2;
      const sy = (img.height - side) / 2;
      const canvas = document.createElement('canvas');
      canvas.width = MAX_DIM;
      canvas.height = MAX_DIM;
      const ctx = canvas.getContext('2d');
      if (ctx === null) {
        reject(new Error('no 2d context'));
        return;
      }
      ctx.drawImage(img, sx, sy, side, side, 0, 0, MAX_DIM, MAX_DIM);
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = dataUrl;
  });
}
